import { ICONS, Icon } from 'components/Icon';
import { ItemDetails } from 'components/ItemDetails';
import { ListItemGeneric } from 'components/List/ListItemGeneric';
import { Pressable } from 'components/Pressable';
import { LocalThumbnail } from 'components/Thumbnail/LocalThumbnail';
import { useAlert } from 'hooks/useAlert';
import { useScreenSize } from 'hooks/useScreenSize';
import { filterArrayEmpty } from 'lib/riskbase';
import { theme } from 'lib/theme';
import { isEqual } from 'lodash';
import * as React from 'react';
import { StyleSheet, View } from 'react-native';
import { AssetListItemProps } from 'screens/AssetsScreen/AssetList/Item/types';

import { ItemMeta } from './ItemMeta';
import { Avatar } from 'components/Avatar';

const ASSET_LIST_ITEM_HEIGHT = 110;

const areEqual = (
  prev: AssetListItemProps,
  next: AssetListItemProps,
): boolean =>
  prev.selected === next.selected &&
  prev.hasChanges === next.hasChanges &&
  prev.asset.id === next.asset.id &&
  prev.remoteThumbnailUrl === next.remoteThumbnailUrl &&
  prev.useLocalThumbnail === next.useLocalThumbnail &&
  prev.onPress === next.onPress &&
  prev.onLongPress === next.onLongPress &&
  prev.onPressThumbnail === next.onPressThumbnail &&
  isEqual(prev.asset.details, next.asset.details) &&
  isEqual(prev.asset.compliance_summary, next.asset.compliance_summary);

export const Item: React.FC<AssetListItemProps> = React.memo(
  ({
    asset,
    hasChanges,
    onLongPress,
    onPress,
    onPressThumbnail,
    organisation,
    remoteThumbnailUrl,
    selected,
    useLocalThumbnail,
  }) => {
    const alert = useAlert();
    const screenSize = useScreenSize(true);

    const address = [
      asset.details.parentName,
      asset.details.address1,
      asset.details.address2,
      asset.details.city,
      asset.details.county,
      asset.details.postcode,
    ]
      .filter(filterArrayEmpty)
      .join(', ');

    return (
      <ListItemGeneric
        selected={selected}
        onPress={onPress}
        onLongPress={onLongPress}
      >
        <View style={styles.row}>
          <View style={{ marginRight: theme.paddingHorizontal, width: 70 }}>
            <Pressable onPress={onPressThumbnail}>
              {useLocalThumbnail ? (
                <LocalThumbnail
                  thumbnailId={asset.details.avatar}
                  width={70}
                  height={70}
                  rounded
                />
              ) : (
                <Avatar
                  key={remoteThumbnailUrl || asset.id}
                  data={
                    remoteThumbnailUrl
                      ? { remoteUrl: remoteThumbnailUrl }
                      : null
                  }
                  size={70}
                />
              )}
            </Pressable>

            {hasChanges && (
              <Pressable
                onPress={() =>
                  alert({
                    text:
                      "Changes you've made to this asset have not been uploaded to RiskBase Cloud yet.\n\n" +
                      'Connect to the internet and tap the sync indicator.',
                  })
                }
                style={{
                  borderRadius: 21,
                  bottom: selected ? undefined : 0,
                  top: selected ? 0 : undefined,
                  height: 21,
                  padding: 1,
                  position: 'absolute',
                  right: 0,
                  width: 21,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme.colors.red,
                  borderColor: theme.colors.white,
                  borderWidth: 1,
                }}
              >
                <Icon
                  source={ICONS.CloudSolid}
                  color={theme.colors.white}
                  size={13}
                />
              </Pressable>
              // <IconCirclePressable
              //   style={{
              //     bottom: selected ? undefined : 0,
              //     position: 'absolute',
              //     right: 0,
              //     top: selected ? 0 : undefined,
              //   }}
              //   color={theme.colors.red}
              //   onPress={() =>
              //     alert({
              //       text:
              //         "Changes you've made to this asset have not been uploaded to RiskBase Cloud yet.\n\n" +
              //         'Connect to the internet and tap the sync indicator.',
              //     })
              //   }
              //   source={ICONS.CloudSolid}
              // />
            )}

            {selected && (
              <Icon
                source={ICONS.SquareCheckSolid}
                style={{
                  bottom: 0,
                  position: 'absolute',
                  right: 0,
                  backgroundColor: theme.colors.white,
                  borderRadius: 4,
                  borderColor: theme.colors.white,
                  borderWidth: 1,
                  overflow: 'hidden',
                }}
                size={21}
                color={theme.colors.blue}
              />
            )}
          </View>

          <View
            style={
              screenSize === 'small' ? styles.contentSmall : styles.content
            }
          >
            <ItemDetails
              screenSize={screenSize}
              subsubtitle={`RB-${asset.reference}${
                asset.details.custom_reference
                  ? ` / ${asset.details.custom_reference}`
                  : ''
              }`}
              title={asset.details.name}
              subtitle={address || undefined}
            />
            <ItemMeta organisation={organisation} asset={asset} />
          </View>
        </View>
      </ListItemGeneric>
    );
  },
  areEqual,
);

const styles = StyleSheet.create({
  content: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'nowrap',
  },
  contentSmall: {
    alignItems: 'flex-start',
    flex: 1,
    flexWrap: 'wrap',
  },
  row: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    minHeight: ASSET_LIST_ITEM_HEIGHT,
    paddingHorizontal: theme.paddingHorizontal,
    paddingVertical: theme.grid / 2,
  },
});
